import { useAuth } from './useAuth';

/**
 * Hook para manejar permisos basados en los módulos del rol del usuario
 */
export const usePermissions = () => {
  const { user } = useAuth();
  
  const userModules = user?.role?.modules || [];
  
  /**
   * Verifica si el usuario tiene acceso a un módulo específico
   * @param {string} moduleName - Nombre del módulo a verificar
   * @returns {boolean} - true si tiene acceso, false si no
   */
  const hasModule = (moduleName) => {
    return userModules.some(module => 
      module.module_name?.toLowerCase().includes(moduleName.toLowerCase())
    );
  };

  /**
   * Verifica si el usuario tiene acceso a cualquiera de los módulos especificados
   * @param {string[]} moduleNames - Array de nombres de módulos
   * @returns {boolean} - true si tiene acceso a al menos uno
   */
  const hasAnyModule = (moduleNames) => {
    return moduleNames.some(moduleName => hasModule(moduleName));
  };

  /**
   * Verifica si el usuario tiene todos los módulos especificados
   * @param {string[]} moduleNames - Array de nombres de módulos
   * @returns {boolean} - true si tiene todos los módulos
   */
  const hasAllModules = (moduleNames) => {
    return moduleNames.every(moduleName => hasModule(moduleName));
  };

  const normalizePermissionText = (value) =>
    String(value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .trim();

  /**
   * Obtiene todos los módulos del usuario
   * @returns {Array} - Array de módulos
   */
  const getAllModules = () => {
    return userModules;
  };

  /**
   * Verifica si el usuario es administrador
   * @returns {boolean} - true si es administrador
   */
  const isAdmin = () => {
    const rawValue =
      user?.is_administrator ??
      user?.isAdministrator ??
      user?.administrator ??
      user?.is_admin;

    if (typeof rawValue === 'boolean') return rawValue;
    if (typeof rawValue === 'number') return rawValue === 1;
    if (typeof rawValue === 'string') {
      const normalized = rawValue.trim().toLowerCase();
      return ['true', '1', 't', 'yes', 'si', 'sí'].includes(normalized);
    }

    const roleName = user?.role?.name || user?.name_rol;
    if (typeof roleName === 'string') {
      return roleName.toLowerCase().includes('admin');
    }

    return false;
  };

  const isDepositoProfile = () => {
    if (isAdmin()) return false;

    const roleName = normalizePermissionText(user?.role?.name || user?.name_rol);
    return roleName.includes('deposito');
  };

  const isTechnicalArea = () => {
    if (isAdmin()) return true;
    if (isDepositoProfile()) return false;

    const roleName = normalizePermissionText(user?.role?.name || user?.name_rol);
    if (
      roleName.includes('area ingenieria') ||
      roleName.includes('ingenieria') ||
      roleName.includes('tecnico') ||
      roleName.includes('tecnica')
    ) {
      return true;
    }

    return userModules.some((module) => {
      const moduleName = normalizePermissionText(module.module_name);
      return (
        moduleName.includes('check area ing') ||
        moduleName.includes('mov. materiales') ||
        moduleName.includes('asignar materiales') ||
        moduleName.includes('modifica stock')
      );
    });
  };

  // Permisos específicos basados en los módulos que vimos en la respuesta
  const permissions = {
    // Módulos principales
    canViewPersonas: hasModule('Personas'),
    canViewContratos: hasModule('Contrato'),
    canViewPlanes: hasModule('Plan'),
    canViewCupones: hasModule('Cupon'),
    canViewFacturas: hasModule('Factura'),
    canViewEntidades: hasModule('Entidades'),
    canViewEstadisticas: hasModule('Estadisticas'),
    canViewObra: hasModule('Modulo Obra'),
    canViewLotes: hasModule('Lotes'),
    canViewStockCompras: hasModule('Stock y compras'),
    canViewInternalOrders: isDepositoProfile() || hasAnyModule([
      'Pedidos tecnicos',
      'external_orders',
      'Stock y compras',
      'Mov. Materiales',
    ]),
    canCreateInternalOrders: isDepositoProfile() || isTechnicalArea() || hasAnyModule([
      'Pedidos tecnicos',
      'external_orders',
      'Stock y compras',
      'Mov. Materiales',
    ]),
    canManageInternalOrders: isTechnicalArea(),
    canCancelExternalOrder: isDepositoProfile() || isTechnicalArea(),
    canCheckExternalOrderStock: isDepositoProfile() || isTechnicalArea(),
    canCreateSupplyOrderDraftFromExternalOrder: isDepositoProfile() || isTechnicalArea(),
    canGenerateConsumptionFromOrder: isDepositoProfile() || isTechnicalArea(),
    canConfirmConsumptions: isDepositoProfile() || isAdmin() || !isDepositoProfile(),
    canViewSupplyOrders: isDepositoProfile() || isTechnicalArea() || hasAnyModule([
      'Stock y compras',
      'Pedidos tecnicos',
      'Mov. Materiales',
    ]),
    canManageSupplyOrders: isAdmin() || !isDepositoProfile(),
    canReceiveSupplyOrders: isDepositoProfile() || isTechnicalArea() || hasAnyModule([
      'Stock y compras',
      'Pedidos tecnicos',
      'Mov. Materiales',
    ]),
    canViewConsumptions: isDepositoProfile() || hasAnyModule([
      'Consumos de stock',
      'Stock y compras',
      'Mov. Materiales',
    ]),
    canManageConsumptions: isAdmin() || !isDepositoProfile(),
    canDeliverConsumptions: isDepositoProfile() || isTechnicalArea() || hasAnyModule([
      'Consumos de stock',
      'Stock y compras',
      'Mov. Materiales',
    ]),
    
    // Administración
    canViewAdministration: hasModule('Administracion'),
    canManageUsers: hasModule('ABM Usuarios'),
    canManageCompanies: hasModule('Empresas'),
    canManageAccess: hasModule('Accesos'),
    canUseMultiBranch: hasModule('Multi-sucursal'),
    
    // Cupones específicos
    canGenerateCoupons: hasModule('Generar y resgistrar'),
    canMassivePrint: hasModule('Impresion masiva'),
    canViewScoring: hasModule('Puntaje-adjudic-regulariz'),
    
    // Obra específicos
    canManageArticles: isDepositoProfile() || hasModule('ABM Articulos'),
    canManageWarehouses: hasModule('ABM Depositos'),
    canModifyStock: !isDepositoProfile() && (isAdmin() || hasModule('Modifica Stock')),
    canManageMaterials: hasModule('Mov. Materiales'),
    canAssignMaterials: hasModule('Asignar materiales'),
    canManageNeighborhoods: hasModule('ABM B°,U.Consumo'),
    canViewAdditionals: hasModule('Adicionales'),
    canManageAudit: hasModule('Modulo auditoria'),
    canManageMachines: hasModule('Modulo gestion maquinas'),
    canEditOrders: hasModule('Editar pedidos'),
    canAuthorizeTechnicalOrders: isTechnicalArea(),
    canAuthorizeConsumption: isTechnicalArea(),
    canEditLots: hasModule('Editar Lotes'),
    
    // Entidades específicos
    canViewSubscribers: hasModule('Suscriptor'),
    canViewLegalEntities: hasModule('Personas juridicas'),
    
    // Estadísticas específicos
    canViewBillingReports: hasModule('Facturacion'),
    canViewCouponReports: hasModule('Cupones'),
    canViewClientReports: hasModule('Clientes'),
    canViewCollectionReports: hasModule('Cobranzas'),
    canViewPlanData: hasModule('Datos de plan'),
    canViewContractReports: hasModule('Informe contratos'),
    canViewAwardReports: hasModule('Informe adjudica'),
    canViewDebtReports: hasModule('Informe saldo de deuda'),
    canViewPaymentReports: hasModule('Informe pagos'),
    canManageMora: hasAnyModule(['Gestión de Mora', 'Gestion de Mora']),
    
    // Admin general
    isAdmin: isAdmin(),
    isDepositoProfile: isDepositoProfile(),
  };

  return {
    hasModule,
    hasAnyModule,
    hasAllModules,
    getAllModules,
    isAdmin,
    isDepositoProfile,
    isTechnicalArea,
    permissions,
    userModules
  };
};

export default usePermissions;
